import { useQuery } from '@tanstack/react-query'
import { useSession } from '@/lib/auth-client'
import { trpc } from '@/lib/trpc'
import { useAdminMode } from '@/context/AdminModeContext'

export type OrganizationRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER' | null

interface OrganizationPermissions {
  // Role information
  role: OrganizationRole
  isOwner: boolean
  isAdmin: boolean
  isMember: boolean
  isViewer: boolean
  isSiteAdmin: boolean
  isAdminMode: boolean

  // Capabilities
  canManageMembers: boolean
  canManageBilling: boolean
  canManageIntegrations: boolean
  canManageSettings: boolean
  canCreateWorkspaces: boolean
  canDeleteOrganization: boolean
  canViewAnalytics: boolean

  // Loading
  isLoading: boolean
}

/**
 * Hook to get organization permissions for the current user
 *
 * @param organizationId - The organization ID to check permissions for
 * @returns OrganizationPermissions object with role and capabilities
 *
 * @example
 * ```tsx
 * const { canManageMembers, role } = useOrganizationPermissions(orgId)
 *
 * if (!canManageMembers) {
 *   return null // Hide members management section
 * }
 * ```
 */
export function useOrganizationPermissions(organizationId?: string): OrganizationPermissions {
  const { data: session } = useSession()
  const { isAdminMode } = useAdminMode()

  // Fetch organization with members
  const { data, isLoading } = useQuery(
    trpc.organizations.getOrganizationWithMembers.queryOptions(
      { id: organizationId || '' },
      {
        enabled: !!organizationId,
      }
    )
  )

  const userId = session?.user?.id
  const isSiteAdmin = session?.user?.role === 'SITE_ADMIN'

  // Find current user's membership
  const membership = data?.organization?.members?.find((m) => m.userId === userId)
  const memberRole = membership?.role?.toString().toUpperCase() as OrganizationRole | undefined

  const role: OrganizationRole = memberRole || null

  // Role checks
  const isOwner = role === 'OWNER'
  const isAdmin = role === 'ADMIN' || isOwner
  const isMember = role === 'MEMBER' || isAdmin
  const isViewer = !!role // Any role can view

  // SITE_ADMIN in admin mode has full permissions
  const hasAdminAccess = isSiteAdmin && isAdminMode

  // Capabilities (OWNER/ADMIN can manage, MEMBER can create workspaces)
  const canManageMembers = isAdmin || hasAdminAccess
  const canManageBilling = isOwner || hasAdminAccess
  const canManageIntegrations = isAdmin || hasAdminAccess
  const canManageSettings = isAdmin || hasAdminAccess
  const canCreateWorkspaces = isMember || hasAdminAccess
  const canDeleteOrganization = isOwner || hasAdminAccess
  const canViewAnalytics = isAdmin || hasAdminAccess

  return {
    // Roles
    role,
    isOwner,
    isAdmin,
    isMember,
    isViewer,
    isSiteAdmin,
    isAdminMode,

    // Capabilities
    canManageMembers,
    canManageBilling,
    canManageIntegrations,
    canManageSettings,
    canCreateWorkspaces,
    canDeleteOrganization,
    canViewAnalytics,

    // Loading
    isLoading,
  }
}
